import isNumber from 'lodash/isNumber'
import find from 'lodash/find'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isRetailClient } from '@utils/get-channel-name'
import {
  highlightTableColumnDoor,
  isCaptureSizeEnabled
} from '@modules/order/utils/table-column-utils'
import { PRODUCT_OPERATIONS_LOADERS } from '@modules/order/stores/view/List/ListDataStore'
import { QuantityCellRef } from '../EnhancedQuantityCell/EnhancedQuantityCell'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import {
  useEnhancedProductStatus,
  useExclusionDoor
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { OrderType } from '@api/common'

interface EnhancedQuantityHookProps {
  rowData: any
  summary: any
  isOrderEditable: boolean
  doorId?: any
  clientType: string
  isProductDetailsView?: boolean
  isProductDetailsHorizontalView?: boolean
  localDoorProduct?: any
  runFocusInputEffect: boolean
  isActive?: boolean
  showProductSizes: any
  itemIndexObjectStore: any
  visibleColIndex?: number
  orderType?: string
  bspId?: string
  selectedViewUiStore?: any
  sizeIndex?: number
  hideSplitQuantity: boolean
  getSizes: Function
  navigateToNextEditableCell?: Function
  navigateToPrevAvailableCell?: Function
  setRowAndColIndex?: Function
  setSizeIndex?: Function
  handleDoorQtyUpdate: Function
  getProductSizeCurve: Function
  splitOrRollupDoorQuantities: Function
  bookUnbookDoors: Function
  handleDoorSizeQtyUpdate: Function
  afterDetailsPageStatusChange?: Function
  beforeQuantityChangeHandler?: Function
  afterQuantityChangeHandler?: Function
  afterBookUnbookDoorHandler?: Function
}

/**
 * Important central business-logic hook for the List View
 */
export const useEnhancedQuantity = ({
  rowData,
  summary,
  isOrderEditable,
  doorId,
  clientType,
  isProductDetailsView,
  isProductDetailsHorizontalView,
  localDoorProduct,
  runFocusInputEffect,
  isActive,
  showProductSizes,
  itemIndexObjectStore,
  visibleColIndex,
  hideSplitQuantity,
  getSizes,
  navigateToNextEditableCell,
  navigateToPrevAvailableCell,
  setRowAndColIndex,
  sizeIndex,
  setSizeIndex,
  handleDoorQtyUpdate,
  getProductSizeCurve,
  splitOrRollupDoorQuantities,
  bookUnbookDoors,
  handleDoorSizeQtyUpdate,
  afterDetailsPageStatusChange,
  beforeQuantityChangeHandler,
  afterQuantityChangeHandler,
  afterBookUnbookDoorHandler,
  orderType
}: EnhancedQuantityHookProps) => {
  const [openDialog, setOpenDialog] = useState(false)
  const { clientOrder } = summary
  const captureSizeEnabled = isCaptureSizeEnabled(summary)
  const rowDataId = rowData?.id
  const { doorOrderProducts = {} } = rowData || {}
  // Get  Door order Id
  const doorOrderId =
    find(summary?.doorOrders, doorOrder => doorOrder?.door?.id === doorId)?.id ||
    summary?.doorOrder?.doorOrderId
  const isProductActive: boolean = useEnhancedProductStatus(rowData)

  const doorProduct = orderType === OrderType.Door ? rowData : doorOrderProducts?.[doorId]
  const isRetail = isRetailClient(clientType)

  const retailClusterId = rowData?.['retailClusters-id']
  const index = rowDataId ? itemIndexObjectStore.getIndexByKey(rowDataId) : null
  const availableProductSizes = rowData && captureSizeEnabled ? getSizes(rowDataId) : []
  const sizes =
    showProductSizes?.[rowDataId] || isProductDetailsView ? availableProductSizes : []

  const isExclusionDoor: boolean = useExclusionDoor(
    rowData?.excludedInDoors,
    summary?.doorOrders,
    doorOrderId
  )

  const isBookingDoor: boolean = itemIndexObjectStore.isObjectDataLoading(
    // BOOKING_ALL_DOORS is intentionally left out, so we don't disable previously-booked doors
    index,
    `${PRODUCT_OPERATIONS_LOADERS.BOOK_DOOR}_${doorOrderId}`
  )

  const isUnbookingDoor: boolean =
    itemIndexObjectStore.isObjectDataLoading(
      index,
      PRODUCT_OPERATIONS_LOADERS.UNBOOKING_ALL_DOORS
    ) ||
    itemIndexObjectStore.isObjectDataLoading(
      index,
      `${PRODUCT_OPERATIONS_LOADERS.UNBOOK_DOOR}_${doorOrderId}`
    )

  // Indicates book or unbook in progress
  const isBookOpPending = isBookingDoor || isUnbookingDoor

  let isBookedOptimistic: boolean
  if (isBookOpPending) {
    isBookedOptimistic = isBookingDoor && !isUnbookingDoor
  } else {
    isBookedOptimistic = isProductDetailsHorizontalView
      ? !!localDoorProduct
      : !!doorProduct
  }

  // ** Business Logic: a door that was booked THEN excluded may be unbooked, but not re-booked. **
  const isHardExcluded = isExclusionDoor && !isBookedOptimistic

  // Hard-disabled states (excludes optimistic)
  const disabled = !isProductActive || !isOrderEditable || isHardExcluded

  // Kept specific to split and rollup buttons. The product quantity is left editable since it's queued.
  const isUpdatingDraft =
    isNumber(index) &&
    (itemIndexObjectStore.isObjectDataLoading(
      index,
      PRODUCT_OPERATIONS_LOADERS.UPDATING_DRAFT_QUANTITY
    ) ||
      itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY
      ) ||
      itemIndexObjectStore.isObjectDataLoading(
        index,
        `${PRODUCT_OPERATIONS_LOADERS.UPDATING_DOOR_DRAFT_QUANTITY}_${doorOrderId}`
      ) ||
      itemIndexObjectStore.isObjectDataLoading(
        index,
        PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
      ))

  const doorToSizeQuantityDistributionToggle = {
    disabled: isUpdatingDraft || !availableProductSizes.length,
    displaySplitOrRollUpIcon: captureSizeEnabled,
    hideSplitDownIcon: hideSplitQuantity && !isRetail
  }

  const highlightDoors = useMemo(() => {
    return highlightTableColumnDoor(
      isRetail,
      isBookedOptimistic,
      doorOrderId,
      summary?.doorOrders || [],
      retailClusterId
    )
  }, [retailClusterId, doorOrderId, summary?.doorOrders, isBookedOptimistic, isRetail])

  const navigate = useCallback(
    (toNext: boolean) => {
      if (toNext) {
        navigateToNextEditableCell?.(index, visibleColIndex)
      } else {
        navigateToPrevAvailableCell?.(index, visibleColIndex)
      }
    },
    [index, visibleColIndex, navigateToNextEditableCell, navigateToPrevAvailableCell]
  )

  const focus = useCallback(() => {
    setRowAndColIndex?.(index, visibleColIndex)
  }, [index, visibleColIndex, setRowAndColIndex])

  const cellRef = useRef<QuantityCellRef>()

  const draftQtyChangeHandler = useCallback(
    async e => {
      beforeQuantityChangeHandler?.()
      await handleDoorQtyUpdate(
        rowDataId,
        doorId,
        getNumberFromLocale(e.target.value),
        doorOrderId,
        orderType,
        captureSizeEnabled
      )
      afterQuantityChangeHandler?.()
    },
    [
      doorId,
      rowDataId,
      doorOrderId,
      orderType,
      captureSizeEnabled,
      beforeQuantityChangeHandler,
      afterQuantityChangeHandler,
      handleDoorQtyUpdate
    ]
  )

  const handleSplitOrRollupDoorsQty = useCallback(
    async isSplitOperation => {
      const canSplitOrRollUp =
        !isSplitOperation || !!Object.keys(getProductSizeCurve(rowDataId, doorId)).length

      if (canSplitOrRollUp) {
        /* Wait a tick to ensure correct time-sequencing of Door Quantity Blur + Split/Rollup Click race condition */
        setTimeout(async () => {
          beforeQuantityChangeHandler?.()
          await splitOrRollupDoorQuantities(
            rowDataId,
            doorId,
            doorOrderId,
            orderType,
            captureSizeEnabled,
            isSplitOperation
          )
          afterQuantityChangeHandler?.()
        }, 100)
      } else {
        setOpenDialog(true)
      }
    },
    [
      doorId,
      doorOrderId,
      orderType,
      rowDataId,
      captureSizeEnabled,
      splitOrRollupDoorQuantities,
      getProductSizeCurve,
      beforeQuantityChangeHandler,
      afterQuantityChangeHandler
    ]
  )

  const bookUnbookStatusChangeHandler = useCallback(
    async e => {
      beforeQuantityChangeHandler?.()
      await bookUnbookDoors(
        clientOrder?.clientOrderId || clientOrder?.id,
        rowDataId,
        [doorOrderId],
        e.target.checked,
        [doorId]
      )
      if (isProductDetailsView) {
        afterDetailsPageStatusChange?.()
      }
      afterBookUnbookDoorHandler?.()
    },
    [
      doorId,
      rowDataId,
      clientOrder,
      doorOrderId,
      isProductDetailsView,
      afterDetailsPageStatusChange,
      bookUnbookDoors,
      beforeQuantityChangeHandler,
      afterBookUnbookDoorHandler
    ]
  )

  const sizeQuantityChangeHandler = useCallback(
    async (sizeId, e) => {
      beforeQuantityChangeHandler?.()
      await handleDoorSizeQtyUpdate(
        rowDataId,
        doorOrderId,
        doorId,
        getNumberFromLocale(e.target.value),
        sizeId,
        orderType,
        captureSizeEnabled
      )
      afterQuantityChangeHandler?.()
    },
    [
      doorId,
      rowDataId,
      doorOrderId,
      orderType,
      captureSizeEnabled,
      handleDoorSizeQtyUpdate,
      beforeQuantityChangeHandler,
      afterQuantityChangeHandler
    ]
  )

  useEffect(() => {
    if (runFocusInputEffect) {
      if (!disabled && isActive) {
        if (!sizes.length) {
          setSizeIndex?.(-1)
        }
        if (sizeIndex === -1) {
          cellRef?.current?.focusQtyInput?.()
        } else {
          cellRef?.current?.focusSizeInput?.(sizeIndex)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runFocusInputEffect, cellRef, isActive, disabled, sizeIndex])

  return {
    isHardExcluded,
    availableProductSizes,
    disabled,
    isBookOpPending,
    isBooked: isBookedOptimistic,
    highlightDoors,
    doorToSizeQuantityDistributionToggle,
    sizes,
    openDialog,
    isProductActive,
    cellRef,
    doorProduct,
    captureSizeEnabled,
    sizeQuantityChangeHandler,
    bookUnbookStatusChangeHandler,
    handleSplitOrRollupDoorsQty,
    draftQtyChangeHandler,
    focus,
    navigate,
    setOpenDialog
  }
}
